/**
 * SMSWiser campaign groups, pure: a country's campaigns gathered by their
 * group key (first-appearance order, members in input order) and labelled
 * the SMSWiser way — "A", "A, B", "A (+N more)".
 * For Media Devoted | Created By Ciro Autiero
 */
import type { TrackerEnum } from "../../trackers/tracker-registry";
import { campaignGroupKey, type GroupableCampaign } from "./campaign-group-key";

export interface NamedGroupableCampaign extends GroupableCampaign {
  name: string;
}

export interface CampaignGroup<T extends NamedGroupableCampaign = NamedGroupableCampaign> {
  key: string;
  label: string;
  /** Every member's name, comma-separated — the tooltip. */
  fullLabel: string;
  trafficProvider: TrackerEnum;
  trafficCampaignId: string | null;
  campaigns: T[];
}

export function campaignGroupLabel(names: readonly string[]): { label: string; fullLabel: string } {
  const fullLabel = names.join(", ");
  return { label: names.length > 2 ? `${names[0]} (+${names.length - 1} more)` : fullLabel, fullLabel };
}

export function groupCampaigns<T extends NamedGroupableCampaign>(campaigns: readonly T[]): CampaignGroup<T>[] {
  const byKey = new Map<string, T[]>();
  for (const c of campaigns) {
    const key = campaignGroupKey(c);
    byKey.set(key, [...(byKey.get(key) ?? []), c]);
  }
  return [...byKey].map(([key, members]) => {
    const first = members[0];
    const tid = first.trafficCampaignId == null ? "" : String(first.trafficCampaignId).trim();
    return {
      key,
      ...campaignGroupLabel(members.map((m) => m.name)),
      trafficProvider: first.trafficProvider ?? "VOLUUM",
      trafficCampaignId: tid === "" ? null : tid,
      campaigns: members,
    };
  });
}
